import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { useSearch, SearchFilters, SearchResult } from '../hooks/useSearch'
import { SearchResultCard } from '../components/SearchResultCard'
import { SearchFiltersPanel } from '../components/SearchFiltersPanel'
import '../styles/search.css'

interface SearchPageProps {
  userId: string | undefined
  onBack: () => void
}

export function SearchPage({ userId, onBack }: SearchPageProps) {
  const navigate = useNavigate()
  const {
    query,
    setQuery,
    results,
    loading,
    error,
    hasSearched,
    debouncedSearch,
    clearSearch
  } = useSearch(userId)

  const [filters, setFilters] = useState<SearchFilters>({})
  const [showFilters, setShowFilters] = useState(false)

  // Search when query or filters change
  useEffect(() => {
    if (query.trim()) {
      debouncedSearch(query, filters)
    }
  }, [query, filters, debouncedSearch])

  const handleArticleClick = (article: SearchResult) => {
    // Navigate to article view
    navigate(`/app#article/${article.id}`)
  }

  const handleBack = () => {
    clearSearch()
    onBack()
  }

  const activeFilterCount = Object.values(filters).filter(Boolean).length

  return (
    <div className="search-page">
      <header className="search-header">
        <button className="back-btn" onClick={handleBack} aria-label="Back">
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M19 12H5M12 19l-7-7 7-7"/>
          </svg>
        </button>

        <div className="search-input-wrapper">
          <span className="search-icon">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <circle cx="11" cy="11" r="8"/>
              <path d="M21 21l-4.35-4.35"/>
            </svg>
          </span>
          <input
            type="search"
            className="search-input"
            placeholder="Search articles..."
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            autoFocus
          />
          {query && (
            <button className="clear-btn" onClick={clearSearch} aria-label="Clear search">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M18 6L6 18M6 6l12 12"/>
              </svg>
            </button>
          )}
        </div>

        <button
          className={`filter-toggle ${activeFilterCount > 0 ? 'active' : ''}`}
          onClick={() => setShowFilters(!showFilters)}
          aria-label="Toggle filters"
        >
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3"/>
          </svg>
          {activeFilterCount > 0 && <span className="filter-count">{activeFilterCount}</span>}
        </button>
      </header>

      {showFilters && (
        <SearchFiltersPanel
          filters={filters}
          onChange={setFilters}
          onClose={() => setShowFilters(false)}
        />
      )}

      <main className="search-results">
        {loading && (
          <div className="search-loading">
            <div className="spinner" />
            <span>Searching...</span>
          </div>
        )}

        {error && (
          <div className="search-error">
            <p>Search error: {error}</p>
            <button onClick={() => debouncedSearch(query, filters, 0)}>
              Try again
            </button>
          </div>
        )}

        {!loading && !error && hasSearched && results.length === 0 && (
          <div className="no-results">
            <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
              <circle cx="11" cy="11" r="8"/>
              <path d="M21 21l-4.35-4.35"/>
              <path d="M8 8l6 6M14 8l-6 6"/>
            </svg>
            <p>No articles found for "{query}"</p>
            <p className="hint">Try different keywords or adjust filters</p>
          </div>
        )}

        {!loading && !hasSearched && (
          <div className="search-prompt">
            <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
              <circle cx="11" cy="11" r="8"/>
              <path d="M21 21l-4.35-4.35"/>
            </svg>
            <p>Search your library</p>
            <p className="hint">Find articles by title, content, or excerpt</p>
          </div>
        )}

        {results.length > 0 && (
          <>
            <p className="results-count">{results.length} result{results.length !== 1 ? 's' : ''}</p>
            <ul className="results-list">
              {results.map(result => (
                <SearchResultCard
                  key={result.id}
                  result={result}
                  query={query}
                  onClick={() => handleArticleClick(result)}
                />
              ))}
            </ul>
          </>
        )}
      </main>
    </div>
  )
}
